import { useCallback } from 'react';
import {
  TableColumnSettings,
  TableCustomFilterModal,
} from '@asset-metrix/portal-ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import {
  deleteTableFilterCustomization,
  mutateTableFilterCustomization,
} from '../../api/portalGateway/tableFilterCustomization';
import { useStore } from '../../store';
import { pixelSizeHelperPortalUi } from '../../utils/pixelSizeHelperPortalUi';

export type TableCustomFilterProps = {
  activeCustomFilterIds: string[];
  customFilterColumns: {
    id: string;
    key: string;
    label: string;
    sublabel?: string;
  }[];
  modalOpened: boolean;
  onClose: () => void;
  widgetId: string;
  widgetFilterInstanceIds: string[];
  withinPortal?: boolean;
};

export function TableCustomFilter({
  activeCustomFilterIds,
  customFilterColumns,
  modalOpened,
  onClose,
  widgetId,
  widgetFilterInstanceIds,
  withinPortal = true,
}: TableCustomFilterProps) {
  const [storeResetFilterSelection, storeSetFilterLoaded] = useStore(
    useShallow((state) => [state.resetFilterSelection, state.setFilterLoaded])
  );

  const resetFilterSelection = useCallback(
    (key?: string) => {
      for (const id of widgetFilterInstanceIds) {
        storeResetFilterSelection(id, key);
      }
    },
    [storeResetFilterSelection]
  );

  const setFilterLoaded = useCallback(
    (loaded = true) => {
      for (const id of widgetFilterInstanceIds) {
        storeSetFilterLoaded(id, loaded);
      }
    },
    [storeSetFilterLoaded]
  );

  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: mutateTableFilterCustomization,
    onMutate: ({ widgetId, data }) => {
      queryClient.setQueryData(['tableFilterCustomization', { widgetId }], {
        data,
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteTableFilterCustomization,
    onMutate: ({ widgetId }) => {
      queryClient.setQueryData(['tableFilterCustomization', { widgetId }], {
        data: {
          columns: [],
        },
      });
    },
  });

  const customFilters: TableColumnSettings[] = [];
  for (const column of customFilterColumns) {
    const { id, label, sublabel } = column;
    customFilters.push({
      id,
      label,
      sublabel,
      isVisible: activeCustomFilterIds.includes(id),
    });
  }

  return (
    <TableCustomFilterModal
      initialValue={customFilters}
      modalOpened={modalOpened}
      onCommit={(newColumnSettings) => {
        const visibleFilters = newColumnSettings.filter(
          (colSetting) => colSetting.isVisible
        );
        if (visibleFilters.length > 0) {
          // if some filters remain visible, update config
          updateMutation.mutate({
            widgetId,
            data: {
              columns: visibleFilters.map((c) => ({
                columnId: c.id,
              })),
            },
          });

          // reset selection for all filters that are not active ANYMORE
          const deselectedFilters = newColumnSettings.filter(
            (colSetting) =>
              !colSetting.isVisible &&
              activeCustomFilterIds.includes(colSetting.id)
          );
          for (const deselectedFilter of deselectedFilters) {
            const key = customFilterColumns.find(
              (column) => column.id === deselectedFilter.id
            )?.key;
            if (key != null) {
              resetFilterSelection(key);
            }
          }
        } else {
          // no filters remain, delete config
          deleteMutation.mutate({ widgetId });
          resetFilterSelection();
          // Set filters to loaded after reset
          setFilterLoaded();
        }
        onClose();
      }}
      onCancel={onClose}
      onReset={() => {
        deleteMutation.mutate({ widgetId });
        resetFilterSelection();
        // Set filters to loaded after reset
        setFilterLoaded();
        onClose();
      }}
      size={pixelSizeHelperPortalUi(650)}
      withinPortal={withinPortal}
    />
  );
}
